/**
 * Turns a saved program into what a TV in the studio should show.
 *
 * The board is read across a room, not at a desk: every number on it has to survive being reduced
 * to a glance. That is the whole reason this file is separate from the component — deciding that
 * three sets of 12/10/8 reads as "12, 10, 8" while three sets of 10-12 reads as one "10-12" is a
 * rule about the PROGRAM, testable on its own, not a detail of how a card is painted.
 */

import { groupExercisesForRendering, isCardioExercise, isCatalystExercise } from '../../../utils/exerciseUtils';
import type { EnrichedExercise, EnrichedTemplate, EnrichedWorkout } from '../../../utils/templateEnrichment';

/** What one card shows as its headline number: "10-12" + "חזרות". */
export interface PrescriptionDisplay {
    /** The number(s), large. */
    value: string;
    /** What the number counts, small, under it. */
    unit: string;
}

export interface BoardCardExercise {
    key: string;
    name: string;
    imageUrl: string | null;
    /** "דגש: חזה" — the muscle group the exercise emphasises, or null when unknown. */
    emphasis: string | null;
    prescription: PrescriptionDisplay;
    /** "3 סטים · מנוחה 90 שנ׳" — the secondary line. */
    detail: string;
    comment: string | null;
}

export interface BoardCard {
    /** Position on the board, 1-based — the numbered badge on the card. */
    number: number;
    /** A superset is ONE card with two exercises in it, exactly as the builder groups it. */
    type: 'single' | 'superset';
    exercises: BoardCardExercise[];
}

export interface BoardDay {
    index: number;
    /** "אימון A" / the workout's Hebrew name. */
    title: string;
    cards: BoardCard[];
    /** Header stats: "6 תרגילים", "2-3 סטים", "60-120 שנ׳ מנוחה". */
    stats: string[];
}

/**
 * The muscle each target maps to on the board.
 *
 * Deliberately COARSER than `targetMuscles` (which says "רגליים-שרשרת קדמית"): a chip read from
 * across a gym has room for one word. The groupings mirror `SecondaryMusclesBreakdown`, which is
 * how the rest of the admin already collapses these — glutes and calves stay out of the general
 * "רגליים" bucket there, and a trainee reading the board cares about exactly that distinction.
 */
export const MUSCLE_BOARD_GROUPS: Record<string, string> = {
    LEGS_QUADS: 'רגליים',
    LEGS_HAMSTRINGS: 'ירך אחורית',
    LEGS_GLUTES: 'ישבן',
    LEGS_ADDUCTORS: 'ישבן',
    LEGS_ABDUCTORS: 'ישבן',
    LEGS_CALVES: 'תאומים',
    FEET: 'כפות רגליים',
    BACK_ROWES: 'גב',
    BACK_PULLS: 'גב',
    BACK_LOWER_BACK: 'גב תחתון',
    CHEST_UPPER: 'חזה',
    CHEST_MIDDLE: 'חזה',
    CHEST_LOWER: 'חזה',
    ARMS_BICEPS: 'יד קדמית',
    ARMS_TRICEPS: 'יד אחורית',
    ARMS_FOREARMS: 'אמות',
    SHOULDERS_FRONT_DELTS: 'כתפיים',
    SHOULDERS_LATERAL_DELTS: 'כתפיים',
    SHOULDERS_REAR_DELTS: 'כתפיים',
    SHOULDERS_TRAPS: 'טרפזים',
    NECK: 'צוואר',
    ABS_OBLIQUES: 'בטן',
    ABS: 'בטן',
    CARDIO: 'קרדיו',
};

/** "דגש: חזה", or null when the program never recorded a target muscle. */
export const emphasisLabel = (exercise: EnrichedExercise): string | null => {
    const group = exercise.targetMuscle ? MUSCLE_BOARD_GROUPS[exercise.targetMuscle] : undefined;
    return group ? `דגש: ${group}` : null;
};

/** Seconds as m:ss — catalyst sets are prescribed as a duration, not a rep count. */
export const formatSeconds = (seconds: number): string => {
    const safe = Math.max(0, Math.round(seconds));
    return `${Math.floor(safe / 60)}:${String(safe % 60).padStart(2, '0')}`;
};

interface BoardInterval {
    minReps?: number | null;
    maxReps?: number | null;
    recommendedReps?: number | null;
}

/**
 * The max the server falls back to when an exercise has no `defaultMaxReps`
 * (`AdminProgramService`: `Optional.ofNullable(exercise.getDefaultMaxReps()).orElse(15)`).
 */
const SERVER_FALLBACK_MAX_REPS = 15;

/**
 * What ONE set actually prescribes, resolved the way the SERVER resolves it.
 *
 * This is the whole reason the board first showed a flat "10" on every card. A template set stores
 * `minReps`/`maxReps` only when the trainer typed one in; otherwise they are null and the only
 * number left on the program is `recommendedReps`, which
 * `createTemplateExerciseFromDefinition` hardcodes to 10 for every new exercise. The RANGE is not
 * on the program at all — it is on the EXERCISE (`defaultMinReps`/`defaultMaxReps`), and the server
 * merges the two when it builds the trainee's program: the set's own range wins, and whatever it
 * leaves blank comes from the exercise (AdminProgramService, minReps/maxReps).
 *
 * So the board reads it the same way. Mirroring that resolution — rather than inventing a spread
 * around the recommended reps — is what makes the number on the wall the number the trainee will
 * be asked for.
 */
const setRepsLabel = (
    interval: BoardInterval | undefined,
    exercise: EnrichedExercise
): string | null => {
    const min = interval?.minReps ?? exercise.defaultMinReps ?? null;
    const max = interval?.maxReps ?? exercise.defaultMaxReps ?? SERVER_FALLBACK_MAX_REPS;

    if (min != null && max != null) return min === max ? String(min) : `${min}-${max}`;

    // No range on either side — all that is left is the single target.
    const single = interval?.recommendedReps ?? exercise.recommendedReps;
    return single != null && single > 0 ? String(single) : null;
};

/**
 * The card's headline number.
 *
 * Per-set ranges are the interesting case: a program that ramps 12 → 10 → 8 is giving an
 * INSTRUCTION ("add weight, drop reps"), and collapsing it to the first set's "12" — or to a
 * meaningless "8-12" — is the one thing a wall board must not do. Identical sets collapse to a
 * single label, because "10-12, 10-12, 10-12" is just noise.
 */
export const prescriptionDisplay = (exercise: EnrichedExercise): PrescriptionDisplay => {
    if (isCardioExercise(exercise)) {
        return { value: String(exercise.durationTime || 0), unit: 'דקות' };
    }

    if (isCatalystExercise(exercise)) {
        const min = exercise.defaultMinDuration ?? 30;
        const max = exercise.defaultMaxDuration ?? 60;
        const value = min === max ? formatSeconds(min) : `${formatSeconds(min)}-${formatSeconds(max)}`;
        return { value, unit: 'לכל סט' };
    }

    // An exercise saved with no intervals still prescribes ONE thing — resolve it as a single set.
    const intervals: Array<BoardInterval | undefined> = exercise.intervals?.length
        ? exercise.intervals
        : [undefined];

    const perSet = intervals
        .map(interval => setRepsLabel(interval, exercise))
        .filter((label): label is string => label != null);

    if (perSet.length === 0) return { value: String(exercise.recommendedReps ?? 0), unit: 'חזרות' };

    const allSame = perSet.every(label => label === perSet[0]);
    return { value: allSame ? perSet[0] : perSet.join(', '), unit: 'חזרות' };
};

/** "3 סטים · מנוחה 90 שנ׳" — whichever halves the program actually recorded. */
export const detailLine = (exercise: EnrichedExercise): string => {
    const parts: string[] = [];
    const sets = exercise.numberOfSets;
    if (sets && sets > 0 && !isCardioExercise(exercise)) {
        parts.push(`${sets} ${sets === 1 ? 'סט' : 'סטים'}`);
    }
    const rest = exercise.restTimeInSec;
    if (rest && rest > 0) parts.push(`מנוחה ${rest} שנ׳`);
    return parts.join(' · ');
};

/** "2-3" when the day mixes them, "3" when it doesn't, null when nothing prescribes any. */
const rangeLabel = (values: number[]): string | null => {
    if (values.length === 0) return null;
    const min = Math.min(...values);
    const max = Math.max(...values);
    return min === max ? String(min) : `${min}-${max}`;
};

/**
 * The picture to put on a WIDE card.
 *
 * An exercise carries two, and they are different shapes: `coverUrl` is the 16:9 frame pulled from
 * its video (VideoThumbnailExtractor, aspectRatio="landscape"), while `imageUrl` is the square-ish
 * catalog thumbnail the rest of the admin renders at 200x200 and 56x56. A board card is landscape,
 * so preferring the square one — which the board did first — crops away the top and bottom of every
 * photo: heads cut off, half a squat. The landscape frame comes first here for that reason, and the
 * thumbnail is the fallback for an exercise with no video.
 */
const boardImage = (exercise: EnrichedExercise): string | null =>
    exercise.coverUrl || exercise.imageUrl || null;

/**
 * How to fit a picture whose shape we only learn once the browser has it.
 *
 * A landscape photo fills a landscape card with almost nothing lost, so it is CROPPED to fill. A
 * square or portrait one cannot be: filling would cut ~45% of its height, and on a board whose job
 * is to show a trainee the movement, that is the half with the person in it. Those are FITTED
 * instead — narrow bars on a dark panel read as nothing, a beheaded lifter does not.
 */
export const imageFit = (naturalWidth: number, naturalHeight: number): 'cover' | 'contain' => {
    if (!naturalWidth || !naturalHeight) return 'cover';
    return naturalWidth / naturalHeight < 1.4 ? 'contain' : 'cover';
};

const toBoardExercise = (exercise: EnrichedExercise, fallbackKey: string): BoardCardExercise => ({
    key: String(exercise.exerciseId ?? fallbackKey),
    name: exercise.exerciseName || `תרגיל ${exercise.exerciseId ?? ''}`.trim(),
    imageUrl: boardImage(exercise),
    emphasis: emphasisLabel(exercise),
    prescription: prescriptionDisplay(exercise),
    detail: detailLine(exercise),
    comment: exercise.comment || null,
});

/** The header stats for one day: exercise count, the sets it asks for, the rest between them. */
export const dayStats = (exercises: EnrichedExercise[]): string[] => {
    const stats: string[] = [
        `${exercises.length} ${exercises.length === 1 ? 'תרגיל' : 'תרגילים'}`,
    ];

    const sets = rangeLabel(
        exercises
            .filter(ex => !isCardioExercise(ex))
            .map(ex => ex.numberOfSets || 0)
            .filter(n => n > 0)
    );
    if (sets) stats.push(`${sets} סטים`);

    const rest = rangeLabel(exercises.map(ex => ex.restTimeInSec || 0).filter(n => n > 0));
    if (rest) stats.push(`${rest} שנ׳ מנוחה`);

    return stats;
};

/** One day of the program, ready to paint. Supersets stay paired, in builder order. */
export const buildBoardDay = (workout: EnrichedWorkout, dayIndex: number): BoardDay => {
    const exercises = [...(workout.exercises || [])].sort(
        (a, b) => (a.index ?? 0) - (b.index ?? 0)
    );

    const cards: BoardCard[] = groupExercisesForRendering(exercises).map((group, i) => {
        if (group.type === 'superset') {
            return {
                number: i + 1,
                type: 'superset' as const,
                exercises: [
                    toBoardExercise(group.exerciseA, `${i}-a`),
                    toBoardExercise(group.exerciseB, `${i}-b`),
                ],
            };
        }
        return {
            number: i + 1,
            type: 'single' as const,
            exercises: [toBoardExercise(group.exercise, `${i}`)],
        };
    });

    return {
        index: dayIndex,
        title: workout.nameHeb || workout.name || `אימון ${dayIndex + 1}`,
        cards,
        stats: dayStats(exercises),
    };
};

/** Every day of the program, in `index` order — the order the days are numbered in the builder. */
export const buildBoardDays = (template: EnrichedTemplate | null | undefined): BoardDay[] => {
    const workouts = [...(template?.workouts || [])].sort((a, b) => (a.index ?? 0) - (b.index ?? 0));
    return workouts.map((workout, i) => buildBoardDay(workout, i));
};

/** Type sizes and grid shape for a board holding `cardCount` cards. */
export interface BoardLayout {
    columns: number;
    rows: number;
    /** px, against the fixed 1600x900 stage — see ProgramBoard. */
    nameSize: number;
    valueSize: number;
    unitSize: number;
    detailSize: number;
    badgeSize: number;
    gap: number;
}

/**
 * How many columns a day gets, and how big its type is.
 *
 * A fixed stage (see ProgramBoard) means this is the ONLY thing standing between a 3-exercise day
 * that looks empty and a 12-exercise day that overflows: the card count picks the grid, the grid
 * picks the type. Sizes step DOWN as the grid grows, never below what stays legible at 3 metres.
 */
export const getBoardLayout = (cardCount: number): BoardLayout => {
    const columns = cardCount <= 1 ? 1
        : cardCount <= 2 ? 2
        : cardCount <= 3 ? 3
        : cardCount <= 4 ? 2
        : cardCount <= 6 ? 3
        : cardCount <= 8 ? 4
        : cardCount <= 9 ? 3
        : cardCount <= 12 ? 4
        : 5;
    const rows = Math.max(1, Math.ceil(cardCount / columns));
    // Type follows whichever axis is tighter — 4 columns of 1 row still has tall cards, but 4
    // columns of 3 rows does not.
    const density = Math.max(columns, rows);

    if (density <= 2) {
        return { columns, rows, nameSize: 40, valueSize: 56, unitSize: 22, detailSize: 22, badgeSize: 56, gap: 24 };
    }
    if (density === 3) {
        return { columns, rows, nameSize: 30, valueSize: 42, unitSize: 18, detailSize: 18, badgeSize: 44, gap: 20 };
    }
    if (density === 4) {
        return { columns, rows, nameSize: 23, valueSize: 32, unitSize: 15, detailSize: 15, badgeSize: 36, gap: 16 };
    }
    return { columns, rows, nameSize: 19, valueSize: 26, unitSize: 13, detailSize: 13, badgeSize: 30, gap: 12 };
};
